import { ChevronDown, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchExportFile,
  useCreateExport,
  useExport,
  type ExportFormat,
  type ReportKind,
  type ReportScope,
} from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { errorText } from "@/lib/errors";

type ExportButtonProps = {
  report: ReportKind;
  scope: ReportScope;
  groupBy?: string[];
  disabled?: boolean;
};

/** Saves a blob under a file name through a temporary link. */
function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Starts an export, waits for the worker and downloads the file. */
export function ExportButton({ report, scope, groupBy, disabled }: ExportButtonProps) {
  const { t } = useTranslation();
  const create = useCreateExport();
  const [exportId, setExportId] = useState<string>();
  const [savedId, setSavedId] = useState<string>();
  const [error, setError] = useState<string>();
  const status = useExport(exportId);
  const started = useRef<string | undefined>(undefined);
  const data = status.data;

  useEffect(() => {
    if (data?.status !== "done" || started.current === data.id) return;
    started.current = data.id;
    fetchExportFile(data.id)
      .then((blob) => saveFile(blob, data.file_name))
      .catch((e: unknown) => setError(errorText(t, e)))
      .finally(() => setSavedId(data.id));
  }, [data, t]);

  const failed = data !== undefined && data.id === exportId && data.status === "failed";
  const busy = create.isPending || (Boolean(exportId) && savedId !== exportId && !failed);
  const message = error ?? (failed ? t("reports.export.failed") : undefined);
  const start = (format: ExportFormat) => {
    setError(undefined);
    create.mutate(
      {
        report,
        format,
        filter: {
          from: scope.from,
          to: scope.to,
          team_id: scope.teamId,
          user_ids: scope.userIds?.length ? scope.userIds : undefined,
          project_ids: report === "time" || report === "activity" ? scope.projectIds : undefined,
          group_by: groupBy as ("user" | "project" | "task" | "date")[] | undefined,
        },
      },
      {
        onSuccess: (e) => setExportId(e.id),
        onError: (e) => setError(errorText(t, e)),
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" disabled={disabled || busy}>
            {busy ? <Loader2 className="animate-spin" /> : <Download />}
            {busy ? t("reports.export.preparing") : t("reports.export.button")}
            {!busy && <ChevronDown className="text-muted" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => start("xlsx")}>
            <FileSpreadsheet /> {t("reports.export.xlsx")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => start("csv")}>
            <FileText /> {t("reports.export.csv")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {message && (
        <Alert variant="danger" className="max-w-sm">
          {message}
        </Alert>
      )}
    </div>
  );
}
